<script setup lang="ts">
import CalendarIcon from '@/assets/icons/CalendarIcon.vue';
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import { getDatePrefixInlineObservation } from '@/utils/date.utils';
import { calculateElementPosition } from '@/utils/dom.utils';
import VueDatePicker from '@vuepic/vue-datepicker';
import { EPosition } from 'common/enum/misc.enum';
import { now } from 'common/utils/date.utils';
import { useField } from 'vee-validate';
import { computed, nextTick, ref, toRefs, useId, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();

const emit = defineEmits<{
  close: [];
  open: [];
  'update:value': [value: string];
}>();

const props = defineProps<{
  value?: string | null;
  name: string;
  label?: string;
  hasErrors?: boolean;
  theme?: 'compact' | 'default' | 'input';
  emptyLabel?: string;
  position?: EPosition;
  disabled?: boolean;
  useCalendarIcon?: boolean;
  textSecondary?: boolean;
  mobileIconOnly?: boolean;
  /** positions the picker menu relative to this element instead of the own trigger */
  anchorElement?: HTMLElement | null;
  /** renders the picker menu at body level, e.g. when the own trigger is hidden */
  teleport?: boolean | string | HTMLElement;
}>();
const { position } = toRefs(props);

const datePickerRef = useTemplateRef('datePickerRef');
const anchorElementRef = useTemplateRef('anchorElementRef');
const inputDomId = useId();
const menuOpen = ref(false);
const isFocused = ref(false);

const { value, handleChange } = useField(() => props.name, undefined, {
  initialValue: props.value ?? now().toISOString(),
});

const handleInput = (event: Date): void => {
  handleChange(event.toISOString());
  emit('update:value', value.value);
};

const handleOpen = (): void => {
  datePickerRef.value?.openMenu();
};

const handleClose = (): void => {
  datePickerRef.value?.closeMenu();
};

const updateMenuPosition = async (): Promise<void> => {
  await nextTick();

  // teleported pickers nest a second wrapper inside the outer one — position the outermost
  const dialogElement = [...document.querySelectorAll('.dp--menu-wrapper')]
    .filter((wrapper) => !wrapper.parentElement?.closest('.dp--menu-wrapper'))
    .at(-1);
  if (dialogElement) {
    const style = calculateElementPosition({
      anchorElement: props.anchorElement ?? anchorElementRef.value,
      element: dialogElement,
      position: position.value ?? EPosition.TOP_RIGHT,
    });

    Object.assign((dialogElement as HTMLElement).style, style);
  }
};

const onOpen = async (): Promise<void> => {
  menuOpen.value = true;
  emit('open');
  await updateMenuPosition();
};

const onClose = async (): Promise<void> => {
  menuOpen.value = false;
  emit('close');
  await updateMenuPosition();
};

const valueFormatted = computed<string>(() =>
  value.value ? getDatePrefixInlineObservation(value.value) : (props.emptyLabel ?? ''),
);

const isLabelFloating = computed(() => {
  return value.value || menuOpen.value;
});

watch(
  () => props.value,
  () => {
    handleChange(props.value);
  },
  { immediate: true },
);

defineExpose({ close: handleClose, open: handleOpen });
</script>

<template>
  <VueDatePicker
    ref="datePickerRef"
    v-model="value"
    month-name-format="long"
    :name="name"
    :enable-time-picker="false"
    :locale="locale"
    :auto-position="false"
    :teleport="teleport"
    :action-row="{ showNow: true, showPreview: false }"
    :clearable="false"
    :disabled="disabled"
    auto-apply
    class="text-body"
    @closed="onClose"
    @open="onOpen"
    @update:model-value="handleInput"
  >
    <template #trigger>
      <div v-if="theme === 'input'" class="relative flex flex-col gap-xs">
        <label
          v-if="label"
          :for="inputDomId"
          class="input-text-label"
          :class="{
            'label-floating': isLabelFloating,
            'text-tint': isFocused,
          }"
          >{{ label }}</label
        >
        <div
          :id="inputDomId"
          ref="anchorElementRef"
          :class="[
            'input-text bg-surface-primary min-h-14',
            'flex items-center justify-between cursor-pointer',
            'outline-inner state-effects',
            hasErrors && 'border-error',
            menuOpen && 'outline-2 outline-tint outline-offset-[-2px]',
            label && 'input-text-labeled',
            disabled && 'input-text-disabled',
          ]"
          tabindex="0"
          @click.stop="handleOpen"
          @keydown.enter.space.prevent="handleOpen"
          @focus="isFocused = true"
          @blur="isFocused = false"
        >
          <div :class="['truncate pr-xs', value ? 'text-body' : 'text-secondary']">
            {{ valueFormatted }}
          </div>
          <div class="w-icon">
            <CalendarIcon
              v-if="useCalendarIcon"
              class="absolute right-[15px] top-1/2 -translate-y-1/2 size-icon-sm text-secondary shrink-0"
            />
            <ChevronDownIcon
              v-else
              :class="['absolute right-[13px] top-1/2 -translate-y-1/2', 'size-icon-sm text-secondary shrink-0']"
            />
          </div>
        </div>
      </div>
      <button
        v-else
        ref="anchorElementRef"
        class="flex items-center badge-as-button py-[5px] px-sm whitespace-nowrap"
        :class="{ 'text-secondary': textSecondary }"
        type="button"
        @click.stop="handleOpen"
        @keydown.enter.stop="handleOpen"
      >
        <CalendarIcon v-if="theme === 'compact'" :class="['size-icon-sm', mobileIconOnly ? 'md:mr-sm' : 'mr-sm']" />
        <span :class="mobileIconOnly && 'max-md:hidden'">{{ valueFormatted }}</span>
        <ChevronDownIcon class="size-icon-sm text-secondary ml-xs" />
      </button>
    </template>
    <template #action-extra="{ selectCurrentDate }">
      <div class="action-row">
        <CustomButton class="select-button" theme="secondary" @click="selectCurrentDate">
          {{ $t('general.today') }}
        </CustomButton>
      </div>
    </template>
  </VueDatePicker>
</template>

<style>
.dp--menu-wrapper {
  position: fixed;
  transition: none !important;
  transform: none !important;
}

.dp__arrow_top {
  display: none;
}

.dp__main {
  --dp-font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.dp__menu {
  --dp-cell-padding: 6px;
  --dp-primary-color: #5c2ee5;
  --dp-hover-color: #ded5fa;
  font-family: inherit;
  font-size: inherit;
  position: unset;
  @apply rounded-panel p-lg shadow-popover;
  border-color: transparent;
}

.dp__calendar_header,
.dp__calendar_header_separator {
  display: none;
}

.dp__btn.dp__month_year_select {
  @apply text-tint;
  font-size: 20px;
}

.dp__month_year_wrap {
  order: 1;
}

.dp--arrow-btn-nav {
  order: 2;
}
</style>
